import React, { useCallback, useEffect, useState } from 'react'
import { DragDropContext, Draggable, DropResult, Droppable } from 'react-beautiful-dnd'
import { useParams } from 'react-router'
import { Link } from 'react-router-dom'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import GripIcon from '../../icons/grip.svg?react'
import LeftArrow from '../../icons/left-arrow.svg?react'
import { RecordFactory } from '../../models/RecordFactory'
import { reorderArray } from '../../utils'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { PSButton, PSButtonPrimary } from '../app/PSButton'
import { fieldQuestionTypeOptions } from '../settings/Options'
import { FieldsEmptyState } from './FieldsEmptyState'

type Props = {
    forOnboarding?: boolean
    forClient?: boolean
}

const viewWillAppear = async ({
    owner,
    urlParams,
    props: { forOnboarding },
}: PocketComponent<Props>) => {
    const fields =
        !!urlParams.formID || forOnboarding
            ? await RecordFactory.getInstance().loadRecordFields(urlParams.formID, false)
            : owner.id !== undefined
              ? await RecordFactory.getInstance().loadFields(
                    owner.id,
                    undefined,
                    true,
                    false,
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                )
              : []
    return { fields }
}

const render = ({
    base: {
        props: { forClient },
        urlParams,
        reload,
        logAndDisplayError,
    },
    data,
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    const navigate = usePSNavigate()
    const params: any = useParams()

    const [updating, setUpdating] = useState(false)
    const [fields, setFields] = useState(data.fields)

    const addNewField = () => {
        if (params.formID) navigate(`/settings/forms/${params.formID}/questions/new`)
        else if (forClient) navigate('/settings/leads/questions/new')
        else navigate(`/settings/forms/questions/new`)
    }

    const onDragEnd = useCallback(
        async (result: DropResult) => {
            if (!result.destination) return

            const source = fields[result.source.index]
            const dest = fields[result.destination.index]

            setFields((f) => reorderArray(f, result.source.index, result.destination!.index))

            // optimistically update our sorted list so the background call looks seamless
            setUpdating(true)
            const movedResult = await RecordFactory.getInstance().moveField(source, dest.seqnum!)
            setUpdating(false)

            if (!movedResult.ok) {
                logAndDisplayError(movedResult.error)

                // reload in case of error so we get the page back to the correct list order
                reload()
                return
            }
        },
        [fields]
    )

    // If there are no fields, show the empty state
    if (fields.length === 0) {
        return (
            <FieldsEmptyState
                onCreateNewField={addNewField}
                onBack={() => {
                    if (urlParams.formID) {
                        navigate(`/settings/forms/${urlParams.formID}/edit`)
                    }
                    else {
                        navigate(-1)
                    }
                }}
            />
        )
    }

    return (
        <div className="servicesSettings">
            <div className="header">
                <PSButton
                    style={{ marginLeft: 0, marginRight: 16 }}
                    onClick={() => {
                        if (urlParams.formID) {
                            navigate(`/settings/forms/${urlParams.formID}/edit`)
                        }
                        else {
                            navigate(-1)
                        }
                    }}
                >
                    <LeftArrow />
                </PSButton>
                <h1>List of questions</h1>
                <PSButtonPrimary style={{ height: 40 }} onClick={() => addNewField()}>
                    Add question
                </PSButtonPrimary>
            </div>
            <div className="separator" />
            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable" isDropDisabled={updating}>
                    {(droppableProps: any) => {
                        return (
                            <div ref={droppableProps.innerRef}>
                                {fields.map((field, index) => {
                                    const typeOption = fieldQuestionTypeOptions.find(
                                        (option) => option.value === field?.type
                                    )
                                    const editURL = params.formID
                                        ? `/settings/forms/${params.formID}/questions/${field.id}/edit`
                                        : forClient
                                          ? `/settings/leads/questions/${field.id}`
                                          : `/settings/forms/questions/${index}/edit`

                                    return (
                                        <Draggable
                                            key={field.id ?? index}
                                            draggableId={`${field.label}-${index}`}
                                            index={index}
                                        >
                                            {(provided: any) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <div className="serviceCard">
                                                        <div className="details">
                                                            <div className="name">
                                                                {field.label}
                                                            </div>
                                                            <div className="rate">
                                                                {typeOption?.label}
                                                            </div>
                                                        </div>
                                                        <div
                                                            className="edit"
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                            }}
                                                        >
                                                            <Link to={editURL} className="edit">
                                                                Edit
                                                            </Link>
                                                            <div
                                                                style={{
                                                                    height: 48,
                                                                    paddingTop: 16,
                                                                    paddingLeft: 24,
                                                                }}
                                                            >
                                                                <GripIcon />
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </Draggable>
                                    )
                                })}
                            </div>
                        )
                    }}
                </Droppable>
            </DragDropContext>
        </div>
    )
}
export const FieldsPage = usePSComponent(render, viewWillAppear)
